<template>
  <div>
    <div class="loader" v-if="isLoading"></div>
    <div class="loadertext" v-if="isLoading">
      {{ $t("loadingTxt") }}...
    </div>
    <div class="main-wrapper" :class="{ 'blur-content': isLoading }">
      <sidebar-component classMenu="Rute"></sidebar-component>

      <div class="page-wrapper">
        <navbar-component></navbar-component>

        <div class="page-content">
          <div class="d-flex justify-content-between align-items-center flex-wrap grid-margin">
            <div class="col-lg-12 col-xl-12 stretch-card">
              <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                  <h4 class="mb-0">Margin Under Threshold</h4>

                   <button  class="btn btn-success float-end btn-xs"
                      style="margin-right: 5px" @click.prevent="downloadData()">
                      <i class="link-icon" data-feather="download"></i>
                      {{ $t("downloadData") }} .xlsx
                    </button>
                </div>

                <div class="card-body">
                    <div class="modern-table-wrapper">

  <table class="modern-table table align-middle">
    <thead>
      <!-- HEADER -->
      <tr>
        <th v-for="col in columns" :key="col.field" @click="handleSort(col)">
          <div class="d-flex align-items-center gap-1">
            {{ col.label }}
            <span v-if="serverParams.sort.field === col.field">
              {{ serverParams.sort.type === 'asc' ? '▲' : '▼' }}
            </span>
          </div>
        </th>
      </tr>

      <!-- FILTER -->
      <tr>
        <th v-for="col in columns" :key="col.field">
          <input
            v-if="col.filterOptions && col.filterOptions.enabled"
            v-model="serverParams.columnFilters[col.field]"
            @keyup.enter="triggerFilter"
            :placeholder="col.filterOptions.placeholder"
            class="form-control form-control-sm"
          />
        </th>
      </tr>
    </thead>

    <tbody>
      <!-- LOADING -->
      <tr v-if="isLoading">
        <td :colspan="columns.length" class="text-center py-4">
          <div class="spinner-border spinner-border-sm text-primary"></div>
          <div class="text-muted mt-1">Loading data...</div>
        </td>
      </tr>

      <!-- EMPTY -->
      <tr v-if="!isLoading && rows.length === 0">
        <td :colspan="columns.length" class="text-center py-4 text-muted">
          Tidak ada data
        </td>
      </tr>

      <!-- DATA -->
      <tr v-for="row in rows" :key="row.loadid">
        <td v-for="col in columns" :key="col.field">

          <!-- ACTION -->
          <span v-if="col.field === 'actions'">
            <button class="btn btn-light btn-sm" @click="showDetail(row)">
              <i class="mdi mdi-eye text-primary"></i>
            </button>
          </span>

          <!-- CURRENCY -->
          <span v-else-if="['payable_rate','billable_rate'].includes(col.field)">
            {{ formatCurrency(row[col.field]) }}
          </span>

          <!-- LOAD ID -->
          <span v-else-if="col.field === 'loadid'">
            {{ row.loadid || "-" }}
          </span>

          <!-- PERCENT -->
          <span v-else-if="col.field === 'pnl_percent'">
            {{ formatPercent(row.pnl_percent) }}
          </span>

          <!-- DATE -->
          <span v-else-if="col.field === 'decission_date'">
            {{ formatDate(row.decission_date) }}
          </span>

          <!-- ESTIMASI -->
          <span v-else-if="col.field === 'estimasi_profit_loss'">
            {{ buildEstimasiProfitLoss(row) }}
          </span>

          <span v-else-if="col.field === 'estimasi_values'">
            {{ buildEstimasiValue(row) }}
          </span>

          <span v-else>
            {{ row[col.field] || '-' }}
          </span>

        </td>
      </tr>
    </tbody>
  </table>

</div>


<div class="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">

  <!-- LEFT -->
  <div class="d-flex align-items-center gap-2">
    <small class="text-muted">
      Showing 
      {{ (serverParams.page - 1) * serverParams.per_page + 1 }} 
      -
      {{ Math.min(serverParams.page * serverParams.per_page, totalRecords) }} 
      of {{ totalRecords }}
    </small>

    <select 
      class="form-select form-select-sm w-auto"
      v-model.number="serverParams.per_page"
      @change="changePerPage"
    >
      <option :value="10">10</option>
      <option :value="50">50</option>
      <option :value="100">100</option>
      <option :value="200">200</option>
    </select>
  </div>

  <!-- RIGHT -->
  <div>
    <button 
      class="btn btn-outline-secondary btn-sm me-2"
      @click="changePage(serverParams.page - 1)"
      :disabled="serverParams.page === 1"
    >
      Prev
    </button>

    <span class="fw-semibold">Page {{ serverParams.page }}</span>

    <button 
      class="btn btn-outline-secondary btn-sm ms-2"
      @click="changePage(serverParams.page + 1)"
      :disabled="serverParams.page * serverParams.per_page >= totalRecords"
    >
      Next
    </button>
  </div>

</div>

                </div>
              </div>
            </div>
          </div>
        </div>

        <footer-component></footer-component>
      </div>
    </div>
  </div>
</template>

<script>
import sidebarComponent from "@/components/_partials/_sidebar";
import navbarComponent from "@/components/_partials/_navbar";
import footerComponent from "@/components/_partials/_footer";

export default {
  name: "MarginUnderThreshold",
  components: {
    "sidebar-component": sidebarComponent,
    "navbar-component": navbarComponent,
    "footer-component": footerComponent,
  },
  data() {
    return {
      isLoading: false,
      errors: [],
      rows: [],
      totalRecords: 0,
      serverParams: {
        columnFilters: {},
        sort: {
          field: "pnl_date",
          type: "desc",
        },
        page: 1,
        per_page: 10,
      },
      columns: [
        {
          label: "Action",
          field: "actions",
          sortable: false,
        },
        {
          label: "Load ID",
          field: "loadid",
          filterOptions: {
            enabled: true,
            placeholder: "Filter By Load ID",
            filterValue: "",
            trigger: "enter",
          },
        },
        {
          label: "Payable Rate",
          field: "payable_rate",
          filterOptions: {
            enabled: true,
            placeholder: "Filter By Payable Rate",
            filterValue: "",
            trigger: "enter",
          },
        },
        {
          label: "Billable Rate",
          field: "billable_rate",
          filterOptions: {
            enabled: true,
            placeholder: "Filter By Billable Rate",
            filterValue: "",
            trigger: "enter",
          },
        },
        {
          label: "Pnl Percent",
          field: "pnl_percent",
          filterOptions: {
            enabled: true,
            placeholder: "Filter By Pnl Percent",
            filterValue: "",
            trigger: "enter",
          },
        },
        {
          label: "Decission Date",
          field: "decission_date",
          filterOptions: {
            enabled: true,
            placeholder: "Filter By Decission Date",
            filterValue: "",
            trigger: "enter",
          },
        },
        {
          label: "Decission User",
          field: "decission_user",
          filterOptions: {
            enabled: true,
            placeholder: "Filter By Decission User",
            filterValue: "",
            trigger: "enter",
          },
        },
        {
          label: "Carrier Name",
          field: "carrier_name",
          filterOptions: {
            enabled: true,
            placeholder: "Filter By Carrier Name",
            filterValue: "",
            trigger: "enter",
          },
        },
        {
          label: "Customer Name",
          field: "customer_name",
          filterOptions: {
            enabled: true,
            placeholder: "Filter By Customer Name",
            filterValue: "",
            trigger: "enter",
          },
        },
        {
          label: "Branch",
          field: "branch",
          filterOptions: {
            enabled: true,
            placeholder: "Filter By Branch",
            filterValue: "",
            trigger: "enter",
          },
        },
        {
          label: "Vehicle Type",
          field: "vehicle_type",
          filterOptions: {
            enabled: true,
            placeholder: "Filter By Vehicle Type",
            filterValue: "",
            trigger: "enter",
          },
        },
        {
          label: "Pick Location",
          field: "pick_location",
          filterOptions: {
            enabled: true,
            placeholder: "Filter By Pick Location",
            filterValue: "",
            trigger: "enter",
          },
        },
        {
          label: "Drop Location",
          field: "drop_location",
          filterOptions: {
            enabled: true,
            placeholder: "Filter By Drop Location",
            filterValue: "",
            trigger: "enter",
          },
        },
        {
          label: "Estimasi Values",
          field: "estimasi_values",
          sortable: false,
        },
        {
          label: "Estimasi Profit / Loss",
          field: "estimasi_profit_loss",
          sortable: false,
        },
      ],
    };
  },
  created() {
    this.loadItems();
  },
  methods: {


    handleSort(col) {
  if (!col.field || col.field === 'actions') return;

  if (this.serverParams.sort.field === col.field) {
    this.serverParams.sort.type =
      this.serverParams.sort.type === 'asc' ? 'desc' : 'asc';
  } else {
    this.serverParams.sort.field = col.field;
    this.serverParams.sort.type = 'asc';
  }

  this.loadItems();
},

triggerFilter() {
  this.serverParams.page = 1;
  this.loadItems();
},

changePage(page) {
  if (page < 1) return;
  this.serverParams.page = page;
  this.loadItems();
},

changePerPage() {
  this.serverParams.page = 1;
  this.loadItems();
},

     downloadData() {
       var file_name = `marginUnderThreshold` + `.xlsx`;
      const filters = this.serverParams.columnFilters || {};
      const params = new URLSearchParams({
        limit: this.serverParams.per_page,
        page: this.serverParams.page,
        sort_field: this.serverParams.sort.field || "",
        sort_type: this.serverParams.sort.type || "",
        loadid: filters.loadid || "",
        payable_rate: filters.payable_rate || "",
        billable_rate: filters.billable_rate || "",
        pnl_percent: filters.pnl_percent || "",
        decission_date: filters.decission_date || "",
        decission_user: filters.decission_user || "",
        carrier_name: filters.carrier_name || "",
        customer_name: filters.customer_name || "",
        branch: filters.branch || "",
        vehicle_type: filters.vehicle_type || "",
        pick_location: filters.pick_location || "",
        drop_location: filters.drop_location || "",
        download:"download",
      });

      const baseURI = this.$settings.endPoint + "transport-order-margin/margin";
      this.isLoading = true;

      return this.$http
        .get(baseURI + "?" + params.toString(), { responseType: "blob" })
        .then((response) => {
          this.isLoading = false;
          var fileURL = window.URL.createObjectURL(new Blob([response.data]));
          var fileLink = document.createElement("a");

          fileLink.href = fileURL;
          fileLink.setAttribute("download", file_name);
          document.body.appendChild(fileLink);
          fileLink.click();
          this.fade(false);
        })
        .catch((error) => {
          this.isLoading = false;
          this.handleHttpError(error);
        });

     
    },

    
    loadItems() {
      const filters = this.serverParams.columnFilters || {};
      const params = new URLSearchParams({
        limit: this.serverParams.per_page,
        page: this.serverParams.page,
        sort_field: this.serverParams.sort.field || "",
        sort_type: this.serverParams.sort.type || "",
        loadid: filters.loadid || "",
        payable_rate: filters.payable_rate || "",
        billable_rate: filters.billable_rate || "",
        pnl_percent: filters.pnl_percent || "",
        decission_date: filters.decission_date || "",
        decission_user: filters.decission_user || "",
        carrier_name: filters.carrier_name || "",
        customer_name: filters.customer_name || "",
        branch: filters.branch || "",
        vehicle_type: filters.vehicle_type || "",
        pick_location: filters.pick_location || "",
        drop_location: filters.drop_location || "",
      });

      const baseURI = this.$settings.endPoint + "transport-order-margin/margin";
      this.isLoading = true;

      return this.$http
        .get(baseURI + "?" + params.toString(), { timeout: 10000 })
        .then((response) => {
          this.isLoading = false;
          this.rows = response.data?.datas?.data || [];
          this.totalRecords = response.data?.datas?.total || 0;
        })
        .catch((error) => {
          this.isLoading = false;
          this.handleHttpError(error);
        });
    },

    onColumnFilter(params) {
      this.updateParams({
        columnFilters: params.columnFilters || {},
        page: 1,
      });
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
    },

    onSortChange(params) {
      this.updateParams({
        sort: {
          type: params[0].type,
          field: params[0].field,
        },
      });
    },

    onPerPageChange(params) {
      this.updateParams({ per_page: params.currentPerPage, page: 1 });
    },

    updateParams(newProps) {
      this.serverParams = Object.assign({}, this.serverParams, newProps);
      this.loadItems();
    },

    formatCurrency(value) {
      return Number(value || 0).toLocaleString("id-ID", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    formatCurrencyWithPrefix(value) {
      return "Rp. " + this.formatCurrency(value);
    },

    formatPercent(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }

      const number = Number(value);
      if (Number.isNaN(number)) {
        return value;
      }

      return number.toLocaleString("id-ID", {
        minimumFractionDigits: number % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }) + "%";
    },

    formatDate(value) {
      if (!value) {
        return "-";
      }

      return this.$moment(value).format("DD/MM/YYYY");
    },

    buildEstimasiValue(row) {
      if (row.estimasi_value) {
        return row.estimasi_value;
      }

      if (row.charge_code || row.min_value || row.max_value) {
        const minValue =
          row.min_value !== null && row.min_value !== undefined
            ? this.formatCompactValue(row.min_value)
            : "";
        const maxValue =
          row.max_value !== null && row.max_value !== undefined
            ? this.formatCompactValue(row.max_value)
            : "";
        const range = [minValue, maxValue].filter(Boolean).join(" - ");
        return [row.charge_code || "", range].filter(Boolean).join(" ");
      }

      return "-";
    },

   

    buildEstimasiProfitLoss(row) {
   
    
      if (row.min_percent !== null && row.max_percent !== null ) {
          const minPercent = this.formatPercent(row.min_percent);
          const maxPercent = this.formatPercent(row.max_percent);
         return minPercent + " s/d " + maxPercent;
      }

     
    },

    formatCompactValue(value) {
      const amount = Number(value || 0);
      if (Number.isNaN(amount)) {
        return value;
      }

      if (amount >= 1000000) {
        return amount / 1000000 + "jt";
      }

      if (amount >= 1000) {
        return Math.round(amount / 1000) + "rb";
      }

      return String(amount);
    },

    showDetail(row) {
      const html = `
        <div style="text-align:left;padding:0 8px;">
          <div style="display:grid;grid-template-columns:1fr 1fr;column-gap:32px;row-gap:10px;font-size:16px;">
            <div>
              ${this.detailItem("Load Id", this.escapeHtml(row.loadid || "-"))}
              ${this.detailItem("Payable Rate", this.escapeHtml(this.formatCurrencyWithPrefix(row.payable_rate)))}
              ${this.detailItem("Billable Rate", this.escapeHtml(this.formatCurrencyWithPrefix(row.billable_rate)))}
              ${this.detailItem("PNL Percent", this.escapeHtml(this.formatPercent(row.pnl_percent)))}
              ${this.detailItem("Decission Date", this.escapeHtml(this.formatDate(row.decission_date)))}
              ${this.detailItem("Decission User", this.escapeHtml(row.decission_user || "-"))}
              ${this.detailItem("Carrier Name", this.escapeHtml(row.carrier_name || "-"))}
            </div>
            <div>
              ${this.detailItem("Customer Name", this.escapeHtml(row.customer_name || "-"))}
              ${this.detailItem("Branch", this.escapeHtml(row.branch || "-"))}
              ${this.detailItem("Vehicle Type", this.escapeHtml(row.vehicle_type || "-"))}
              ${this.detailItem("Pick Location", this.escapeHtml(row.pick_location || "-"))}
              ${this.detailItem("Drop Location", this.escapeHtml(row.drop_location || "-"))}
              ${this.detailItem("Estimasi Value", this.escapeHtml(this.buildEstimasiValue(row)))}
              ${this.detailItem("Estimasi Profit / Loss", this.escapeHtml(this.buildEstimasiProfitLoss(row)))}
            </div>
          </div>
        </div>
      `;

      this.$swal.fire({
        title: "VIEW MARGIN TRANSACTION",
        html,
        width: 900,
        confirmButtonText: "close",
        confirmButtonColor: "#18a61b",
      });
    },

    detailItem(label, value) {
      return `
        <div style="display:grid;grid-template-columns:120px 1fr;gap:8px;margin-bottom:10px;">
          <div style="font-weight:500;">${label}</div>
          <div>${value}</div>
        </div>
      `;
    },

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },

    handleHttpError(error) {
      if (error.code === "ECONNABORTED") {
        this.error(this.$t("check_internet_connection_txt"));
        return;
      }

      if (!error.response) {
        this.error("Terjadi kesalahan pada koneksi.");
        return;
      }

      if (error.response.status === 500) {
        this.$router.push("/server-error");
      } else if (error.response.status === 401) {
        this.$router.push("/authorized-error");
      } else {
        this.$router.push("/page-not-found");
      }
    },

    error(message) {
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true,
      });

      Toast.fire({
        icon: "error",
        title: message,
      });
    },
  },
};
</script>

<style scoped>
.modern-table-wrapper {
  max-height: 800px;
  overflow: auto;
  border-radius: 10px;
  border: 1px solid #e9ecef;
}

/* HEADER */
.modern-table thead th {
  position: sticky;
  background: #f8f9fb;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  padding: 10px;
  z-index: 10;
  white-space: nowrap;
}

/* HEADER */
.modern-table thead tr:first-child th {
  top: 0;
}

/* FILTER */
.modern-table thead tr:nth-child(2) th {
  top: 37px;
  background: #fff;
}

/* BODY */
.modern-table tbody td {
  font-size: 13px;
  padding: 10px;
  vertical-align: middle;
}

/* HOVER */
.modern-table tbody tr:hover {
  background: #f1f3f5;
  transition: 0.15s;
}

/* BUTTON */
.btn-light {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
}

.btn-light:hover {
  background: #e9ecef;
}
</style>
